import { useCallback, useRef, useState } from "react";
import { useReactFlow } from "@xyflow/react";
import type { OnNodeDrag } from "@xyflow/react";
import { useRootStore } from "../../../stores/hooks";
import * as A from "../../../stores/automation/mergers/actions";
import type { GroupLocator } from "../../../stores/automation/utils/action-group-walk";
import type { CandidateStore } from "../../../stores/automation/candidate.store";
import type { ActionCandidate } from "../../../types/candidate";
import { useFlowCanvasHandlers } from "../nodes/context";
import type { FlowNode, FlowNodeData } from "../graph/types";
import { CARD_VGAP } from "../graph/constants";
import { DEFAULT_CARD_HEIGHT } from "../graph/layout";

type ActionNodeData = Extract<FlowNodeData, { kind: "action" }>;

/** A sibling action card's geometry, in flow coordinates. */
interface SiblingGeometry {
  id: string;
  y: number;
  height: number;
}

/**
 * Slot `i` means "insert before sibling `i`". The dragged card's center is
 * compared against each sibling's center (`y + height / 2`); the slot is the
 * number of sibling centers above the dragged center. Pure — the single unit
 * of drop math, tested directly.
 */
export const computeDropSlot = (
  draggedCenterY: number,
  siblings: SiblingGeometry[],
): number =>
  siblings.reduce(
    (count, sibling) =>
      sibling.y + sibling.height / 2 < draggedCenterY ? count + 1 : count,
    0,
  );

interface ReorderPlan {
  sourceIndex: number;
  destinationIndex: number;
}

/**
 * Pure decision core for a drop. `otherSiblings` are the container's action
 * cards WITH the dragged card excluded, at their layout geometry; the slot
 * computed against them is exactly the post-removal insertion index that
 * `moveAction(source, destination)` expects. Returns `null` when the card
 * lands on its original slot (destination === source) — the no-op the legacy
 * react-beautiful-dnd path also skips.
 */
export const planReorder = (
  sourceIndex: number,
  draggedCenterY: number,
  otherSiblings: SiblingGeometry[],
): ReorderPlan | null => {
  if (sourceIndex < 0) return null;
  const destinationIndex = computeDropSlot(draggedCenterY, otherSiblings);
  return destinationIndex === sourceIndex
    ? null
    : { sourceIndex, destinationIndex };
};

interface DropIndicator {
  x: number;
  y: number;
  width: number;
}

interface UseNodeReorderResult {
  onNodeDragStart: OnNodeDrag;
  onNodeDrag: OnNodeDrag;
  onNodeDragStop: OnNodeDrag;
  dropIndicatorY: number | null;
  dropIndicatorX: number | null;
  dropIndicatorWidth: number | null;
}

const asActionData = (node: FlowNode): ActionNodeData | undefined =>
  node.data.kind === "action" ? (node.data as ActionNodeData) : undefined;

const sameLocator = (a: GroupLocator, b: GroupLocator): boolean => {
  if (a.kind !== b.kind || a.instanceId !== b.instanceId) return false;
  if (a.kind === "branch" && b.kind === "branch") {
    return a.branchHash === b.branchHash;
  }
  return true;
};

const heightOf = (node: FlowNode): number =>
  node.measured?.height ?? DEFAULT_CARD_HEIGHT;

/** Direct action cards of the container the locator addresses, in store
 *  order — the same list `setActionsAt(locator, ...)` writes back into. */
const directActionsAt = (
  candidate: CandidateStore,
  locator: GroupLocator,
): ActionCandidate[] => {
  switch (locator.kind) {
    case "root":
      return candidate.getGroupActions("actions");
    case "loop":
      return candidate.looping.loopActionsAt(locator.instanceId);
    case "branch":
      return candidate.branches.branchActions({
        instanceId: locator.instanceId,
        branchHash: locator.branchHash,
      });
  }
};

interface DragContext {
  locator: GroupLocator;
  actions: ActionCandidate[];
  sourceIndex: number;
  others: Array<SiblingGeometry & { x: number; width: number }>;
  draggedCenterY: number;
}

const computeIndicator = (
  slot: number,
  others: Array<SiblingGeometry & { x: number; width: number }>,
): DropIndicator | null => {
  if (others.length === 0) return null;
  const sorted = [...others].sort((a, b) => a.y - b.y);
  const ref = sorted[0];
  let y: number;
  if (slot <= 0) {
    y = sorted[0].y - CARD_VGAP / 2;
  } else if (slot >= sorted.length) {
    const last = sorted[sorted.length - 1];
    y = last.y + last.height + CARD_VGAP / 2;
  } else {
    const prev = sorted[slot - 1];
    y = prev.y + prev.height + CARD_VGAP / 2;
  }
  return { x: ref.x, y, width: ref.width };
};

export const useNodeReorder = (): UseNodeReorderResult => {
  const store = useRootStore();
  const handlers = useFlowCanvasHandlers();
  const { getNodes, updateNode } = useReactFlow();
  const [indicator, setIndicator] = useState<DropIndicator | null>(null);
  const dragOriginRef = useRef<Map<string, { x: number; y: number }>>(
    new Map(),
  );

  const collect = useCallback(
    (node: FlowNode): DragContext | null => {
      const data = asActionData(node);
      if (data === undefined) return null;

      const candidate = store.automations.candidate;
      const actions = directActionsAt(candidate, data.locator);
      const sourceIndex = actions.findIndex(
        (action) => action.hash === data.actionHash,
      );
      if (sourceIndex < 0) return null;

      // Other siblings stay at their layout positions during a drag (only the
      // dragged node moves), so getNodes() gives their stable geometry.
      const others = getNodes()
        .filter((sibling): sibling is FlowNode => sibling.id !== node.id)
        .map((sibling) => ({ sibling, data: asActionData(sibling) }))
        .filter(
          (entry): entry is { sibling: FlowNode; data: ActionNodeData } =>
            entry.data !== undefined &&
            sameLocator(entry.data.locator, data.locator),
        )
        .map(({ sibling }) => ({
          id: sibling.id,
          x: sibling.position.x,
          y: sibling.position.y,
          width: sibling.measured?.width ?? sibling.width ?? 0,
          height: heightOf(sibling),
        }));

      return {
        locator: data.locator,
        actions,
        sourceIndex,
        others,
        draggedCenterY: node.position.y + heightOf(node) / 2,
      };
    },
    [store, getNodes],
  );

  const onNodeDragStart = useCallback<OnNodeDrag>((_event, node) => {
    dragOriginRef.current.set(node.id, { ...node.position });
  }, []);

  const onNodeDrag = useCallback<OnNodeDrag>(
    (_event, node) => {
      const context = collect(node as FlowNode);
      if (context === null) {
        setIndicator(null);
        return;
      }
      const slot = computeDropSlot(context.draggedCenterY, context.others);
      setIndicator(computeIndicator(slot, context.others));
    },
    [collect],
  );

  const onNodeDragStop = useCallback<OnNodeDrag>(
    (_event, node) => {
      setIndicator(null);
      const context = collect(node as FlowNode);
      if (context === null) return;
      const plan = planReorder(
        context.sourceIndex,
        context.draggedCenterY,
        context.others,
      );
      if (plan === null) {
        const origin = dragOriginRef.current.get(node.id);
        if (origin !== undefined) {
          updateNode(node.id, { position: origin });
        }
        dragOriginRef.current.delete(node.id);
        return;
      }
      dragOriginRef.current.delete(node.id);

      const candidate = store.automations.candidate;
      const actionToMove = context.actions[plan.sourceIndex];

      // Legacy eject guard (use-action-drag-drop.ts:56-75), replicated
      // verbatim per container: when a record-list-referencing action is
      // dragged UP past its referenced source action, the now-forward
      // reference is ejected first. Quirks preserved for parity: the
      // destination-index-vs-instanceId comparison and the truthy
      // referencedActionIndex check (index 0 never ejects).
      if (
        actionToMove.recordList?.recordList.oneofKind === "actionReference" &&
        plan.destinationIndex < plan.sourceIndex &&
        plan.destinationIndex <=
          actionToMove.recordList.recordList.actionReference.instanceId
      ) {
        const referencedActionIndex = context.actions.findIndex(
          (action) =>
            actionToMove.recordList?.recordList.oneofKind ===
              "actionReference" &&
            actionToMove.recordList.recordList.actionReference.instanceId ===
              action.actionReference?.instanceId,
        );
        if (
          referencedActionIndex &&
          plan.destinationIndex <= referencedActionIndex
        ) {
          candidate.setActionsAt(context.locator, [
            A.ejectActionReferencesInCurrentAction(actionToMove.hash),
          ]);
        }
      }

      // Mirror the legacy react-beautiful-dnd drop: the moveAction merger
      // routed to the dragged card's own container. Positions then snap back
      // from layout automatically (no persisted drag offset).
      candidate.setActionsAt(context.locator, [
        A.moveAction(plan.sourceIndex, plan.destinationIndex),
      ]);

      // Legacy post-move cleanup (use-action-drag-drop.ts:78): strip
      // action-reference-sourced record-list inputs, routed the way each
      // legacy container wires setActionsWithOutReferences — root scope for
      // the root list AND branch columns (BranchColumn.tsx:96-100 passes the
      // root-scoped candidate method), the loop's own actions for loops
      // (Looping/index.tsx:115-122; the *At variant addresses the dragged
      // loop by instanceId, identical for the legacy singleton root loop).
      if (context.locator.kind === "loop") {
        candidate.looping.setActionsWithOutReferencesAt(
          context.locator.instanceId,
          "actionReference",
        );
      } else {
        candidate.setActionsWithOutReferences("actionReference");
      }

      // Legacy selects the moved action after the drop
      // (use-action-drag-drop.ts:79).
      handlers.onClickAction(actionToMove.hash);
    },
    [collect, store, handlers, updateNode],
  );

  return {
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    dropIndicatorY: indicator?.y ?? null,
    dropIndicatorX: indicator?.x ?? null,
    dropIndicatorWidth: indicator?.width ?? null,
  };
};
